import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';

import pencilIcon from 'assets/pencil_edit.svg';

import { buildRefinementMessage } from 'utlis/chatPrototype';
import { REFINEMENT_PROMPT_MAX_LENGTH } from 'utlis/constants';

import styles from './styles.module.scss';

const DEFAULT_CUSTOM_LABEL = 'Something else';
const EXIT_ANIMATION_MS = 150;

export function RefinementOptionsPanel({ options, customInputLabel, isHidden, onCancel, onContinue }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [isCustomOpen, setIsCustomOpen] = useState(false);
  const [customText, setCustomText] = useState('');
  const [isClosing, setIsClosing] = useState(false);

  const panelRef = useRef(null);
  const customInputRef = useRef(null);
  const closeTimeoutRef = useRef(null);

  const canContinue = selectedIds.length > 0 || customText.trim() !== '';
  const customLabel = customInputLabel || DEFAULT_CUSTOM_LABEL;

  useEffect(() => () => clearTimeout(closeTimeoutRef.current), []);

  useEffect(() => {
    if (!isHidden) {
      panelRef.current?.focus();
    }
  }, [isHidden]);

  useEffect(() => {
    if (isCustomOpen) {
      customInputRef.current?.focus();
    }
  }, [isCustomOpen]);

  const closeThen = useCallback(action => {
    if (closeTimeoutRef.current) {
      return;
    }

    setIsClosing(true);
    closeTimeoutRef.current = setTimeout(action, EXIT_ANIMATION_MS);
  }, []);

  const handleToggle = useCallback(id => {
    setSelectedIds(ids => (ids.includes(id) ? ids.filter(selectedId => selectedId !== id) : [...ids, id]));
  }, []);

  const handleOpenCustom = useCallback(() => setIsCustomOpen(true), []);

  const handleCustomChange = useCallback(event => setCustomText(event.target.value), []);

  const handleCancel = useCallback(() => closeThen(onCancel), [closeThen, onCancel]);

  const handleContinue = useCallback(() => {
    if (!canContinue) {
      return;
    }

    const labels = options.filter(option => selectedIds.includes(option.id)).map(option => option.label);

    closeThen(() => onContinue(buildRefinementMessage(labels, customText)));
  }, [canContinue, closeThen, customText, onContinue, options, selectedIds]);

  const handleKeyDown = useCallback(event => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      handleCancel();
    }
  }, [handleCancel]);

  const handleCustomKeyDown = useCallback(event => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
      event.preventDefault();
      handleContinue();
    }
  }, [handleContinue]);

  return (
    <div
      ref={panelRef}
      className={cn(styles['refinement-panel'], {
        [styles['refinement-panel--closing']]: isClosing,
        [styles['refinement-panel--hidden']]: isHidden,
      })}
      aria-hidden={isHidden}
      role="dialog"
      aria-label="What should be included in this variant?"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      <p className={styles['refinement-panel__title']}>
        What should be included in this variant?
      </p>
      <div className={styles['refinement-panel__options']}>
        {options.map((option, index) => {
          const isSelected = selectedIds.includes(option.id);

          return (
            <button
              key={`${option.id}-${index}`}
              type="button"
              role="checkbox"
              aria-checked={isSelected}
              className={styles['refinement-panel__option']}
              onClick={() => handleToggle(option.id)}
            >
              <span
                className={cn(styles['refinement-panel__badge'], {
                  [styles['refinement-panel__badge--selected']]: isSelected,
                })}
              >
                {index + 1}
              </span>
              <span className={styles['refinement-panel__label']}>
                {option.label}
              </span>
            </button>
          );
        })}
        {isCustomOpen
          ? (
            <div className={styles['refinement-panel__option']}>
              <span
                className={cn(styles['refinement-panel__badge'], {
                  [styles['refinement-panel__badge--selected']]: customText.trim() !== '',
                })}
              >
                <img src={pencilIcon} alt="" className={styles['refinement-panel__icon']} />
              </span>
              <input
                ref={customInputRef}
                type="text"
                className={styles['refinement-panel__input']}
                placeholder={customLabel}
                value={customText}
                maxLength={REFINEMENT_PROMPT_MAX_LENGTH}
                onChange={handleCustomChange}
                onKeyDown={handleCustomKeyDown}
              />
            </div>
          )
          : (
            <button type="button" className={styles['refinement-panel__option']} onClick={handleOpenCustom}>
              <span className={styles['refinement-panel__badge']}>
                <img src={pencilIcon} alt="" className={styles['refinement-panel__icon']} />
              </span>
              <span className={cn(styles['refinement-panel__label'], styles['refinement-panel__label--muted'])}>
                {customLabel}
              </span>
            </button>
          )}
      </div>
      <div className={styles['refinement-panel__footer']}>
        <button
          type="button"
          className={cn(styles['refinement-panel__button'], styles['refinement-panel__button--cancel'])}
          onClick={handleCancel}
        >
          Cancel
        </button>
        <button
          type="button"
          className={cn(styles['refinement-panel__button'], styles['refinement-panel__button--continue'])}
          disabled={!canContinue}
          onClick={handleContinue}
        >
          Continue
        </button>
      </div>
    </div>
  );
}

RefinementOptionsPanel.propTypes = {
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
  })).isRequired,
  customInputLabel: PropTypes.string,
  isHidden: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onContinue: PropTypes.func.isRequired,
};

RefinementOptionsPanel.defaultProps = {
  customInputLabel: undefined,
  isHidden: false,
};
